package com.downdogapp

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.encoding.CompositeDecoder
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlin.jvm.JvmInline
import kotlin.math.abs
import kotlin.math.roundToLong

@Serializable(with = DurationSerializer::class)
@JvmInline
value class Duration(val seconds: Double) : Comparable<Duration> {

  val millis get() = (seconds * 1_000).roundToLong()
  val minutes get() = seconds / 60
  val hours get() = minutes / 60
  val days get() = hours / 24

  operator fun plus(rhs: Duration) = Duration(this.seconds + rhs.seconds)
  operator fun minus(rhs: Duration) = Duration(this.seconds - rhs.seconds)

  operator fun times(rhs: Number) = Duration(this.seconds * rhs.toDouble())
  operator fun times(rhs: IntRange): DurationRange = times(rhs.start)..times(rhs.endInclusive)
  operator fun times(rhs: DoubleRange): DurationRange = times(rhs.start)..times(rhs.endInclusive)

  operator fun div(rhs: Duration) = this.seconds / rhs.seconds
  operator fun div(rhs: Number) = Duration(this.seconds / rhs.toDouble())

  override operator fun compareTo(other: Duration) = this.seconds.compareTo(other.seconds)

  override fun toString() =
    when {
      seconds < 60 -> "${seconds.toString(2)} sec"
      seconds < 3600 -> "${minutes.toString(2)} min"
      else -> "${hours.toString(2)} hours"
    }

  companion object {

    val ZERO = Duration(0.0)
  }
}

object DurationSerializer : KSerializer<Duration> {

  private val dataSerializer = Double.serializer()

  override val descriptor = buildClassSerialDescriptor("DurationSerializer") {
    element("seconds", dataSerializer.descriptor)
  }

  override fun serialize(encoder: Encoder, value: Duration) {
    val output = encoder.beginStructure(descriptor)
    output.encodeSerializableElement(descriptor, 0, dataSerializer, value.seconds)
    output.endStructure(descriptor)
  }

  override fun deserialize(decoder: Decoder): Duration {
    val input = decoder.beginStructure(descriptor)
    var seconds = 0.0
    while (true) {
      when (val i = input.decodeElementIndex(descriptor)) {
        0 -> seconds = input.decodeSerializableElement(descriptor, i, dataSerializer)
        CompositeDecoder.DECODE_DONE -> break
        else -> error("Unexpected index: $i")
      }
    }
    input.endStructure(descriptor)
    return Duration(seconds)
  }
}

fun abs(duration: Duration): Duration = Duration(abs(duration.seconds))

fun minutes(minutes: Number) = seconds(minutes.toDouble() * 60)

@Suppress("NOTHING_TO_INLINE")
inline fun seconds(seconds: Int) = Duration(seconds.toDouble())

@Suppress("NOTHING_TO_INLINE")
inline fun seconds(seconds: Double) = Duration(seconds)

fun millis(millis: Number) = Duration(millis.toDouble() / 1000)

fun hours(hours: Number) = minutes(hours.toDouble() * 60)

typealias DurationRange = ClosedRange<Duration>

fun hours(range: DoubleRange) =
  hours(range.start)..hours(range.endInclusive)

fun hours(range: IntRange) =
  hours(range.first)..hours(range.last)

fun minutes(range: DoubleRange) =
  minutes(range.start)..minutes(range.endInclusive)

fun minutes(range: IntRange) =
  minutes(range.first)..minutes(range.last)

fun seconds(range: DoubleRange) =
  seconds(range.start)..seconds(range.endInclusive)

fun seconds(range: IntRange) =
  seconds(range.first)..seconds(range.last)

fun millis(range: LongRange) =
  millis(range.first)..millis(range.last)

fun millis(range: IntRange) =
  millis(range.first)..millis(range.last)

val DurationRange.seconds get() = start.seconds..endInclusive.seconds

val DurationRange.minutes get() = start.minutes..endInclusive.minutes

val DurationRange.millis get() = start.millis..endInclusive.millis

val DurationRange.size get() = endInclusive - start

fun Iterable<Duration>.sum() = sumOfDurations { it }

fun Sequence<Duration>.sum() = sumOfDurations { it }

fun Iterable<Duration>.average() = seconds(map { it.seconds }.average())

fun Sequence<Duration>.average() = seconds(map { it.seconds }.average())

fun <T> Iterable<T>.sumOfDurations(selector: (T) -> Duration): Duration {
  var sum = 0.0
  for (element in this) {
    sum += selector(element).seconds
  }
  return seconds(sum)
}

fun <T> Sequence<T>.sumOfDurations(selector: (T) -> Duration): Duration {
  var sum = 0.0
  for (element in this) {
    sum += selector(element).seconds
  }
  return seconds(sum)
}
